"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Button } from "../button";
import { Input } from "../input";
import { Label } from "../label";
import { Textarea } from "../textarea";
import { LoadingSpinner } from "../loading-spinner";

// Public booking specific types
interface PublicBookingData {
  email: string;
  description: string;
  location: string;
  isOnline?: boolean;
  bookeeEmail: string;
  slot: {
    start: string;
    end: string;
  };
}

interface BookingFormProps {
  selectedSlot: string | null;
  bookeeEmail: string;
  onSubmit: (data: PublicBookingData) => Promise<void>;
  isLoading?: boolean;
  isPublic?: boolean;
  prefilledName?: string;
  prefilledEmail?: string;
  prefilledLocation?: string;
  isLocationEditable?: boolean;
  isOnline?: boolean;
}

export function BookingForm({
  selectedSlot,
  bookeeEmail,
  onSubmit,
  isLoading = false,
  isPublic = true,
  prefilledName,
  prefilledEmail,
  prefilledLocation,
  isLocationEditable = true,
  isOnline = false,
}: BookingFormProps) {
  const [formData, setFormData] = useState<PublicBookingData>({
    email: prefilledEmail || "",
    description: "",
    location: prefilledLocation || "",
    bookeeEmail: bookeeEmail,
    slot: { start: "", end: "" },
    isOnline: isOnline,
  });

  // Update bookeeEmail when prop changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, bookeeEmail }));
  }, [bookeeEmail]);

  // Update form data when prefilled values change
  useEffect(() => {
    if (prefilledEmail) {
      setFormData(prev => ({ ...prev, email: prefilledEmail }));
    }
  }, [prefilledEmail]);

  useEffect(() => {
    if (prefilledLocation !== undefined) {
      setFormData(prev => ({ ...prev, location: prefilledLocation }));
    }
  }, [prefilledLocation]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, isOnline }));
  }, [isOnline]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedSlot) {
      return;
    }

    await onSubmit(formData);
  };

  const isFormValid = formData.email && formData.description && selectedSlot;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Book Your Meeting</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {prefilledName && (
            <div>
              <Label htmlFor="name">Your Name</Label>
              <Input
                id="name"
                type="text"
                value={prefilledName}
                disabled
                className="bg-muted"
              />
            </div>
          )}

          <div>
            <Label htmlFor="email">Your Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="your.email@example.com"
              required
              disabled={!!prefilledEmail}
              className={prefilledEmail ? "bg-muted" : ""}
            />
          </div>

          <div>
            <Label htmlFor="description">Meeting Description *</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="What is this meeting about?"
              required
            />
          </div>

          <div>
            <Label htmlFor="location">
              {isOnline ? "Meeting Type" : "Location"} {!isLocationEditable && "(Pre-set)"}
            </Label>
            <Input
              id="location"
              value={formData.location || (isOnline ? "Online" : "")}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              placeholder={isOnline ? "Online meeting" : "Meeting location or video call link"}
              disabled={!isLocationEditable}
              className={!isLocationEditable ? "bg-muted" : ""}
            />
          </div>

          {selectedSlot && (
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground">
                <strong>Selected Time:</strong> {new Date(selectedSlot).toLocaleString()}
              </p>
            </div>
          )}

          <Button 
            type="submit" 
            disabled={!isFormValid || isLoading} 
            className="w-full"
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Booking...
              </>
            ) : (
              "Book Meeting"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
